/**
 * The AI report generate request, shared by `MemoReportForm` and the desktop project record's
 * "Generate a job summary": the notes' ids, run in the background past 15 notes, refused past 100,
 * and the report period from the notes filters' dates.
 */
import { CreateAiReportReq, type Memo } from '@kalos/kalos-rpc';
import { format } from 'date-fns/esm';

/** The most notes one report can be made from. */
export const AI_REPORT_MAX_NOTES = 100;
/** Past this many notes the report is made in the background. */
export const AI_REPORT_BACKGROUND_AFTER_NOTES = 15;
const JOB_SUMMARY_PROMPT_ID = 'pmpt_6939c4f7d02481978014988f10b709cf0e2926d05623a266';

/** Extract start/end calendar dates from the operator-prefixed dateRange tuple (e.g. ['>=', '2026-01-01', '<', '2026-04-08']). */
export function extractPeriodFromDateRange(dateRange?: string[]): {
  start: string;
  end: string;
} {
  let start = '';
  let end = '';
  if (!dateRange || dateRange.length === 0) return { start, end };
  for (let i = 0; i < dateRange.length - 1; i++) {
    const op = dateRange[i];
    const val = dateRange[i + 1];
    if ((op === '>=' || op === '>') && val) {
      start = val.slice(0, 10);
    } else if ((op === '<' || op === '<=') && val) {
      end = val.slice(0, 10);
    }
  }
  // If start exists but end is open-ended, default to today
  if (start && !end) {
    end = format(new Date(), 'yyyy-MM-dd');
  }
  return { start, end };
}

/**
 * The generate request for these notes, or `tooLarge` past `AI_REPORT_MAX_NOTES`.
 * `memoRequest` is the notes filter the report was asked from (its job and dates).
 */
export const buildAiReportRequest = ({
  memos,
  memoRequest,
  customQuery,
  userId,
}: {
  memos: Memo[];
  memoRequest: Memo;
  customQuery: string;
  userId: number;
}): { tooLarge: true; noteCount: number } | { tooLarge: false; req: CreateAiReportReq } => {
  // Compile array of memo IDs
  const memoIds = memos
    .map((memo) => memo.id)
    .filter((id): id is number => id !== undefined && id !== 0);

  // Validate memo count - prevent generation if more than 100
  if (memoIds.length > AI_REPORT_MAX_NOTES) {
    return { tooLarge: true, noteCount: memoIds.length };
  }

  // Determine if we should run in background (> 15 notes)
  const shouldRunInBackground = memoIds.length > AI_REPORT_BACKGROUND_AFTER_NOTES;

  // Convert memo IDs to CSV string
  const memoIdsCsv = memoIds.join(',');

  const { start: periodStart, end: periodEnd } = extractPeriodFromDateRange(memoRequest.dateRange);

  const req = CreateAiReportReq.create({
    reportPromptId: JOB_SUMMARY_PROMPT_ID,
    input: customQuery || '',
    eventId: memoRequest.eventId || 0,
    memoIds,
    variables: {
      creator: userId.toString(),
      memo_ids: memoIdsCsv,
      instructions: customQuery || '',
    },
    background: shouldRunInBackground,
    createdAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
    reportPeriodStart: periodStart,
    reportPeriodEnd: periodEnd,
  });
  return { tooLarge: false, req };
};
